import { AbstractControl, ValidatorFn } from '@angular/forms';

const validGenderSelectOptions = ['M', 'F', 'O', 'N', 'P'];

const SCANNING_CREATION_DATE = new Date(2017, 0, 1);
const OLDEST_ALLOWED_BIRTH_DATE = new Date(1900, 0, 1);

export const GenderValidator: ValidatorFn =
  (control: AbstractControl): { [key: string]: boolean } => {
    if (isValidGender(control.value)) {
      return null;
    }
    return {gender: true};
  };

export function isValidGender(gender: string): boolean {
  return validGenderSelectOptions.indexOf(gender) > -1;
}

export const BirthDateValidator: ValidatorFn =
  (control: AbstractControl): { [key: string]: boolean } => {
    if (isValidBirthDate(control.value)) {
      return null;
    }
    return {date: true};
  };

/* If required==true, will not allow the date to be empty */
export function makeTestDateValidator(required: boolean, name: string = 'N/A'): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } => {
    if (isValidTestDateOrBlank(control.value, required, name)) {
      return null;
    }
    return {date: true};
  };
}

export const ChildSpecifierValidator: ValidatorFn  = (control: AbstractControl): { [key: string]: boolean } => {
  const specifier = control.value;
  if (specifier === null || specifier === '' || specifier === '-' || specifier === '*') {
    return {specifier: true};
  }
  return null;
}

function isDateInRange(target: Date, lower: Date, upper: Date) {
  return target >= lower && target <= upper;
}

/* Expects Raw Biographical Data String Date MM/DD/YYYY. Function only exported for testing. */
export function isValidMDYDate(mdyDateStr: string, oldestAllowedDate: Date): boolean {
  // Check the expected format.
  const dateRegEx = new RegExp(/^\d{1,2}\/\d{1,2}\/\d{4}$/);
  const parsedDate = new Date(mdyDateStr);
  if (!dateRegEx.test(mdyDateStr) || /Invalid|NaN/.test(parsedDate.toString())) {
    return false;
  }

  const [month, day, year] = mdyDateStr.split('/');
  const parsedIsValid = isValidParsedDate(year, month, day, parsedDate);
  if (! parsedIsValid) {
    return false;
  }

  // Ensure the date is not unexpectedly far in the past.
  // Ensure the date is not in the future because we don't expect such dates.
  return isDateInRange(parsedDate, oldestAllowedDate, new Date());
}

/* Function only exported for testing. */
export function isValidParsedDate(rawYear: string, rawMonth: string, rawDay: string, parsedDate: Date): boolean {
  if (!rawYear || !rawMonth || !rawDay || !parsedDate) {
    return false;
  }

  // Convert to numbers so we can do math on the month.
  const year = parseInt(rawYear, 10);
  // Months must be zero based.
  const month = parseInt(rawMonth, 10) - 1;
  const day = parseInt(rawDay, 10);

  // The Date constructor does not perform all obvious validations such as flagging a date of February 30th.
  // In such a case it will change the date.  ex. 2/30/2018 => 3/2/2018.
  // So, ensure the individual values have not changed.
  return month === parsedDate.getUTCMonth() && day === parsedDate.getUTCDate() && year === parsedDate.getUTCFullYear();
}

/* Function only exported for testing. */
export function isValidISODate(isoDateStr: string): boolean {
  let date: Date;
  let isoString;
  try {
    date = new Date(isoDateStr);
    // throws an error on some invalid dates
    isoString = date.toISOString();
  } catch {
    return false;
  }

  if (isoString) {
    if (isoString[10] !== 'T') {
      // It is not in ISO format.
      return false;
    }
    const [splitDateStr] = isoString.split('T');
    const [year, month, day] = splitDateStr.split('-');
    return isValidParsedDate(year, month, day, new Date(splitDateStr));
  }
  return false;
}

export function isValidMDYBirthDate(mdyDateStr: string) {
  return mdyDateStr && isValidMDYDate(mdyDateStr, OLDEST_ALLOWED_BIRTH_DATE);
}

export function isValidMDYTestDateOrBlank(mdyDateStr: string) {
  return !mdyDateStr || isValidMDYDate(mdyDateStr, SCANNING_CREATION_DATE);
}

/* Function only exported for testing. */
export function isValidBirthDate(date: Date) {
  if (date) {
    let justDateISOString: string;

    try {
      // toISOString will throw an error on invalid dates, so we need to wrap it in a try catch block
      justDateISOString = date.toISOString().split('T')[0];
    } catch {
      return false;
    }

    if (isValidISODate(justDateISOString)) {
      return isDateInRange(date, OLDEST_ALLOWED_BIRTH_DATE, new Date());
    }
  }

  return false;
}

/* Function only exported for testing. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isValidTestDateOrBlank(date: Date, required: boolean, name: string = 'N/A') {
  if (date?.toString().indexOf('Invalid') >= 0) {
    // this will present when an 'invalid date placeholder' date of -/-/- is used
    return false;
  }
  if (! required && ! date) {
    return true;
  }

  if (date) {
    let justDateISOString: string;

    try {
      // toISOString will throw an error on invalid dates, so we need to wrap it in a try catch block
      justDateISOString = date.toISOString().split('T')[0];
    } catch {
      return false;
    }

    if (isValidISODate(justDateISOString)) {
      return isDateInRange(date, SCANNING_CREATION_DATE, new Date());
    }
  }

  return false;
}
